// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// compassClientFilter — Compass (#191) client-side filter matcher.
//
// On the MONGO backend the raw filter box runs server-side (real find()). On
// the CONVEX backend there is no ad-hoc server-side filter (only indexed
// columns), so the raw filter is applied to the loaded page IN THE BROWSER by
// this matcher. It supports a useful subset of Mongo query semantics:
//
//   - field equality:            { statusCode: "4" }
//   - dotted paths:              { "Materials.type": "Carpet" }
//   - operators per field:       $eq $ne $in $nin $gt $gte $lt $lte
//                                $exists $regex (+ $options "i")
//   - logical (top level):       $and / $or / $nor  (arrays of sub-filters)
//
// Pure + side-effect-free. Anything it can't express returns false-safe (a
// doc that the matcher can't confidently match is excluded), and the UI tells
// the user the Convex raw filter is page-local + best-effort.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export function getByPath(obj, path) {
  if (obj == null) return undefined;
  if (path.indexOf(".") === -1) return obj[path];
  let cur = obj;
  for (const part of path.split(".")) {
    if (cur == null) return undefined;
    cur = cur[part];
  }
  return cur;
}

function asComparable(v) {
  // Dates → epoch ms so $gt/$lt work; everything else as-is.
  if (v instanceof Date) return v.getTime();
  return v;
}

function matchesOperators(value, ops) {
  for (const [op, operand] of Object.entries(ops)) {
    switch (op) {
      case "$eq":
        if (!looseEqual(value, operand)) return false;
        break;
      case "$ne":
        if (looseEqual(value, operand)) return false;
        break;
      case "$in":
        if (!Array.isArray(operand) || !operand.some((o) => looseEqual(value, o)))
          return false;
        break;
      case "$nin":
        if (Array.isArray(operand) && operand.some((o) => looseEqual(value, o)))
          return false;
        break;
      case "$gt":
        if (!(asComparable(value) > asComparable(operand))) return false;
        break;
      case "$gte":
        if (!(asComparable(value) >= asComparable(operand))) return false;
        break;
      case "$lt":
        if (!(asComparable(value) < asComparable(operand))) return false;
        break;
      case "$lte":
        if (!(asComparable(value) <= asComparable(operand))) return false;
        break;
      case "$exists":
        if (operand ? value === undefined : value !== undefined) return false;
        break;
      case "$regex": {
        const flags = ops.$options && /i/.test(ops.$options) ? "i" : "";
        let re;
        try {
          re = new RegExp(operand, flags);
        } catch (_e) {
          return false;
        }
        if (value == null || !re.test(String(value))) return false;
        break;
      }
      case "$options":
        // handled alongside $regex
        break;
      default:
        // Unknown operator — can't confidently match.
        return false;
    }
  }
  return true;
}

// Loose equality so a string "4" matches a number 4 (Mongo statusCode is
// stored as either; the catalog's raw-filter users routinely type strings).
function looseEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== "object" && typeof b !== "object") {
    return String(a) === String(b);
  }
  return false;
}

function isOperatorObject(v) {
  return (
    v !== null &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    Object.keys(v).every((k) => k.startsWith("$"))
  );
}

export function matchesClientFilter(doc, filter) {
  if (!filter || typeof filter !== "object") return true;
  for (const [key, cond] of Object.entries(filter)) {
    if (key === "$and") {
      if (!Array.isArray(cond) || !cond.every((f) => matchesClientFilter(doc, f)))
        return false;
      continue;
    }
    if (key === "$or") {
      if (!Array.isArray(cond) || !cond.some((f) => matchesClientFilter(doc, f)))
        return false;
      continue;
    }
    if (key === "$nor") {
      if (!Array.isArray(cond) || cond.some((f) => matchesClientFilter(doc, f)))
        return false;
      continue;
    }
    const value = getByPath(doc, key);
    if (isOperatorObject(cond)) {
      if (!matchesOperators(value, cond)) return false;
    } else if (!looseEqual(value, cond)) {
      return false;
    }
  }
  return true;
}

export function applyClientFilter(docs, filter) {
  if (!Array.isArray(docs)) return [];
  if (!filter || Object.keys(filter).length === 0) return docs;
  return docs.filter((d) => matchesClientFilter(d, filter));
}
